import type { ReactNode } from "react";
import styles from "./ui.module.css";
import { Button } from "./Button";

export type StateKind =
  | "pending"
  | "empty"
  | "error"
  | "stale"
  | "unauthenticated"
  | "forbidden"
  | "offline";

type Props = {
  kind: StateKind;
  title: string;
  message?: string;
  detail?: string;
  actionLabel?: string;
  onAction?: () => void;
  children?: ReactNode;
};

export function StatePanel({
  kind,
  title,
  message,
  detail,
  actionLabel,
  onAction,
  children,
}: Props) {
  if (kind === "pending") {
    return (
      <div className={styles.panel} aria-busy="true" aria-live="polite">
        <p className={styles.panelTitle}>{title}</p>
        <div className={styles.skeleton} style={{ width: "70%", marginBottom: 8 }} />
        <div className={styles.skeleton} style={{ width: "45%" }} />
        {children}
      </div>
    );
  }

  const tone =
    kind === "forbidden" || kind === "error"
      ? styles.panelDanger
      : kind === "unauthenticated"
        ? styles.panelInfo
        : kind === "offline"
          ? styles.panelOffline
          : kind === "stale"
            ? styles.panelWarn
            : "";

  return (
    <div className={[styles.panel, tone].filter(Boolean).join(" ")} role="status">
      <h2 className={styles.panelTitle}>{title}</h2>
      {message ? <p className={styles.panelBody}>{message}</p> : null}
      {detail ? <p className={`mono ${styles.meta}`}>{detail}</p> : null}
      {actionLabel && onAction ? (
        <div className={styles.actions} style={{ justifyContent: "flex-start", marginTop: "0.75rem" }}>
          <Button
            variant={kind === "unauthenticated" ? "primary" : kind === "forbidden" ? "secondary" : "primary"}
            onClick={onAction}
          >
            {actionLabel}
          </Button>
        </div>
      ) : null}
      {children}
    </div>
  );
}
